import { useState } from 'react';
import {
  type Address,
  type WizardOrderData,
  GEOGRAPHIC_SCOPES,
  DELIVERY_PROFILES,
  DELIVERY_TYPES,
  PACKAGE_SIZES,
  PACKAGE_WEIGHTS,
  PAYMENT_METHODS
} from '@/domains/client/constants/orderWizard.constants';

interface NewOrderWizardProps {
  isOpen: boolean;
  onClose: () => void;
  savedAddresses: Address[];
  onOrderCreated?: () => Promise<void> | void;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

export default function NewOrderWizard({ isOpen, onClose, savedAddresses, onOrderCreated }: NewOrderWizardProps) {
  // Helpers de parsing d'adresse
  const getAddressName = (address: Address) => address.nom_adresse || address.name || 'Adresse';
  const getAddressValue = (address: Address) => address.adresse_complete || address.address || '';
  const getAddressIcon = (address: Address) => address.icon || 'ri-map-pin-line';

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successTrackingCode, setSuccessTrackingCode] = useState<string | null>(null);

  const [orderData, setOrderData] = useState<WizardOrderData>({
    geographicScope: 'urban',
    deliveryType: '',
    deliveryProfile: 'community',
    pickupAddress: '',
    deliveryAddress: '',
    useCurrentLocation: false,
    packageDetails: {
      size: '',
      weight: '',
      specialInstructions: '',
      declaredValue: undefined,
      insuranceEnabled: false
    },
    paymentMethod: ''
  });

  const calculatePrice = (): number => {
    const scope = GEOGRAPHIC_SCOPES.find(s => s.id === orderData.geographicScope);
    const profile = DELIVERY_PROFILES.find(p => p.id === orderData.deliveryProfile);
    const type = DELIVERY_TYPES.find(t => t.id === orderData.deliveryType);

    const basePrice = scope?.basePrice || 2000;
    const profileMultiplier = profile?.priceMultiplier || 1;
    const typeMultiplier = type?.priceMultiplier || 1;

    let totalPrice = basePrice * profileMultiplier * typeMultiplier;

    // Add insurance fee if enabled
    if (orderData.packageDetails.insuranceEnabled && orderData.packageDetails.declaredValue) {
      const insuranceFee = orderData.packageDetails.declaredValue * 0.05; // 5% insurance fee
      totalPrice += insuranceFee;
    }

    return Math.round(totalPrice);
  };

  const nextStep = () => {
    setError('');
    if (currentStep === 1 && !orderData.geographicScope) {
      setError('Veuillez sélectionner la portée géographique.');
      return;
    }
    if (currentStep === 2 && !orderData.deliveryProfile) {
      setError('Veuillez sélectionner le type de livreur.');
      return;
    }
    if (currentStep === 3 && !orderData.deliveryType) {
      setError('Veuillez sélectionner un type de livraison.');
      return;
    }
    if (currentStep === 4 && (!orderData.pickupAddress || !orderData.deliveryAddress)) {
      setError('Les adresses d\'enlèvement et de destination sont requises.');
      return;
    }
    if (currentStep === 5 && (!orderData.packageDetails.size || !orderData.packageDetails.weight)) {
      setError('Veuillez spécifier le gabarit (taille et poids) du colis.');
      return;
    }

    if (currentStep < 6) setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setError('');
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleUseCurrentLocation = () => {
    setError('');
    if (!navigator.geolocation) {
      setError('La géolocalisation n\'est pas supportée par votre navigateur.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setOrderData(prev => ({
          ...prev,
          pickupAddress: `Position Géographique (${latitude.toFixed(5)}, ${longitude.toFixed(5)})`,
          useCurrentLocation: true
        }));
      },
      (geoError) => {
        let msg = 'Impossible d\'accéder à votre position.';
        if (geoError.code === geoError.PERMISSION_DENIED) {
          msg = 'Accès géolocalisation refusé. Veuillez vérifier les permissions de votre navigateur.';
        }
        setError(msg);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleSubmit = async () => {
    if (!orderData.paymentMethod) {
      setError('Veuillez sélectionner un mode de règlement.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const userToken = localStorage.getItem('user_token');
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      const clientId = userData.id;

      if (!userToken || !clientId) {
        throw new Error('Votre session a expiré. Veuillez vous reconnecter.');
      }

      const response = await fetch(`${API_BASE_URL}/commandes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          portee_geographique: orderData.geographicScope,
          profil_livreur: orderData.deliveryProfile,
          type_livraison: orderData.deliveryType,
          adresse_ramassage: orderData.pickupAddress,
          adresse_livraison: orderData.deliveryAddress,
          taille_colis: orderData.packageDetails.size,
          poids_colis: orderData.packageDetails.weight,
          instructions_speciales: orderData.packageDetails.specialInstructions,
          methode_paiement: orderData.paymentMethod,
          description: orderData.packageDetails.specialInstructions || 'Livraison de marchandises',
          prix_estime: calculatePrice(),
          valeur_declaree: orderData.packageDetails.declaredValue,
          assurance_activee: orderData.packageDetails.insuranceEnabled,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 422 && data.errors) {
          throw new Error(Object.values(data.errors).flat().join(' '));
        }
        throw new Error(data.message || 'Échec de l\'enregistrement de la commande.');
      }

      setSuccessTrackingCode(data.code_suivi || 'Créée');
      await onOrderCreated?.();
    } catch (err: any) {
      setError(err.message || 'Une anomalie réseau est survenue.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAndReset = () => {
    setCurrentStep(1);
    setSuccessTrackingCode(null);
    setError('');
    setOrderData({
      geographicScope: 'urban',
      deliveryType: '',
      deliveryProfile: 'community',
      pickupAddress: '',
      deliveryAddress: '',
      useCurrentLocation: false,
      packageDetails: {
        size: '',
        weight: '',
        specialInstructions: '',
        declaredValue: undefined,
        insuranceEnabled: false
      },
      paymentMethod: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-50 animate-fade-in">
      <div className="bg-white w-full sm:max-w-3xl rounded-t-2xl sm:rounded-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-slide-up">

        {/* Header */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900 uppercase tracking-wide">Nouvel Envoi</h2>
            <p className="text-xs text-slate-400 mt-0.5">Étape {currentStep} sur 6 &bull; Remplissage des caractéristiques</p>
          </div>
          <button onClick={handleCloseAndReset} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-800 rounded-xl hover:bg-slate-100 transition-all">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Stepper Propre */}
        <div className="px-6 py-3.5 border-b border-slate-100 bg-white">
          <div className="flex items-center justify-between max-w-lg mx-auto mb-3">
            {[1, 2, 3, 4, 5, 6].map((step) => {
              const isPassed = step <= currentStep;
              return (
                <div key={step} className="flex items-center flex-1 last:flex-none">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${isPassed ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'
                    }`}>
                    {step}
                  </div>
                  {step < 6 && (
                    <div className={`h-0.5 flex-1 mx-1.5 transition-all ${step < currentStep ? 'bg-slate-900' : 'bg-slate-100'
                      }`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden">
            <div className="bg-teal-600 h-full transition-all duration-300" style={{ width: `${(currentStep / 6) * 100}%` }} />
          </div>
        </div>

        {/* Corps des étapes */}
        <div className="p-6 overflow-y-auto flex-1 no-scrollbar space-y-4">

          {error && (
            <div className="p-3.5 bg-rose-50 border border-rose-100 text-rose-700 rounded-xl text-xs font-semibold flex items-center space-x-2">
              <i className="ri-error-warning-fill text-base flex-shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          {/* ÉCRAN DE SUCCÈS INTÉGRÉ */}
          {successTrackingCode ? (
            <div className="py-8 text-center max-w-sm mx-auto space-y-4 animate-fade-in">
              <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-sm border border-emerald-100">
                <i className="ri-checkbox-circle-line text-3xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">Demande enregistrée avec succès</h3>
                <p className="text-xs text-slate-400 mt-1">Le système recherche actuellement un coursier disponible pour prendre en charge votre colis.</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 font-mono text-xs font-bold text-slate-700 select-all">
                Suivi : {successTrackingCode}
              </div>
              <button onClick={handleCloseAndReset} className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition-all">
                Fermer le guichet
              </button>
            </div>
          ) : (
            <>
              {/* ÉTAPE 1 : PORTEE GÉOGRAPHIQUE */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Portée géographique</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {GEOGRAPHIC_SCOPES.map((scope) => {
                      const isSelected = orderData.geographicScope === scope.id;
                      return (
                        <div
                          key={scope.id}
                          onClick={() => { setError(''); setOrderData({ ...orderData, geographicScope: scope.id as 'urban' | 'interurban' }); }}
                          className={`p-4 border rounded-xl cursor-pointer transition-all flex flex-col justify-between ${isSelected ? 'border-slate-900 bg-slate-900/5 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-300'
                            }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-9 h-9 ${scope.color} rounded-xl flex items-center justify-center text-white text-base flex-shrink-0 shadow-sm`}>
                              <i className={scope.icon}></i>
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-xs font-bold text-slate-800">{scope.name}</h4>
                              <p className="text-[11px] text-slate-400 truncate mt-0.5">{scope.description}</p>
                              <p className="text-xs font-bold text-teal-600 mt-1">À partir de {scope.basePrice.toLocaleString()} GNF</p>
                            </div>
                          </div>
                          <ul className="mt-3 pt-2.5 border-t border-dashed border-slate-200/60 space-y-1">
                            {scope.features.map((feat, idx) => (
                              <li key={idx} className="flex items-center text-[10px] text-slate-500">
                                <i className="ri-checkbox-blank-circle-fill text-[4px] text-teal-500 mr-2"></i>
                                {feat}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ÉTAPE 2 : PROFIL DE LIVREUR */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Type de livreur</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {DELIVERY_PROFILES.map((profile) => {
                      const isSelected = orderData.deliveryProfile === profile.id;
                      return (
                        <div
                          key={profile.id}
                          onClick={() => { setError(''); setOrderData({ ...orderData, deliveryProfile: profile.id as 'community' | 'professional' }); }}
                          className={`p-4 border rounded-xl cursor-pointer transition-all flex flex-col justify-between ${isSelected ? 'border-slate-900 bg-slate-900/5 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-300'
                            }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-9 h-9 ${profile.color} rounded-xl flex items-center justify-center text-white text-base flex-shrink-0 shadow-sm`}>
                              <i className={profile.icon}></i>
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-xs font-bold text-slate-800">{profile.name}</h4>
                              <p className="text-[11px] text-slate-400 truncate mt-0.5">{profile.description}</p>
                              <p className="text-xs font-bold text-teal-600 mt-1">
                                {profile.priceMultiplier < 1 ? `Réduction de ${Math.round((1 - profile.priceMultiplier) * 100)}%` : 'Tarif standard'}
                              </p>
                            </div>
                          </div>
                          <ul className="mt-3 pt-2.5 border-t border-dashed border-slate-200/60 space-y-1">
                            {profile.features.map((feat, idx) => (
                              <li key={idx} className="flex items-center text-[10px] text-slate-500">
                                <i className="ri-checkbox-blank-circle-fill text-[4px] text-teal-500 mr-2"></i>
                                {feat}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ÉTAPE 3 : TYPE DE LIVRAISON */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Type de livraison</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {DELIVERY_TYPES.map((type) => {
                      const isSelected = orderData.deliveryType === type.id;
                      return (
                        <div
                          key={type.id}
                          onClick={() => { setError(''); setOrderData({ ...orderData, deliveryType: type.id }); }}
                          className={`p-4 border rounded-xl cursor-pointer transition-all flex flex-col justify-between ${isSelected ? 'border-slate-900 bg-slate-900/5 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-300'
                            }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`w-9 h-9 ${type.color} rounded-xl flex items-center justify-center text-white text-base flex-shrink-0 shadow-sm`}>
                              <i className={type.icon}></i>
                            </div>
                            <div className="min-w-0">
                              <h4 className="text-xs font-bold text-slate-800">{type.name}</h4>
                              <p className="text-[11px] text-slate-400 truncate mt-0.5">{type.description}</p>
                              <p className="text-xs font-bold text-teal-600 mt-1">Multiplicateur: x{type.priceMultiplier}</p>
                            </div>
                          </div>
                          <ul className="mt-3 pt-2.5 border-t border-dashed border-slate-200/60 space-y-1">
                            {type.features.map((feat, idx) => (
                              <li key={idx} className="flex items-center text-[10px] text-slate-500">
                                <i className="ri-checkbox-blank-circle-fill text-[4px] text-teal-500 mr-2"></i>
                                {feat}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ÉTAPE 4 : ADRESSES / GÉOLOCALISATION */}
              {currentStep === 4 && (
                <div className="space-y-5">
                  {/* Ramassage */}
                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Point de collecte / Enlèvement</label>
                    <button
                      type="button"
                      onClick={handleUseCurrentLocation}
                      className="w-full p-3 bg-slate-50 border border-slate-200 hover:border-slate-400 rounded-xl transition-all flex items-center space-x-3 text-left"
                    >
                      <i className="ri-map-pin-user-line text-teal-600 text-lg"></i>
                      <div>
                        <p className="text-xs font-bold text-slate-800">Capturer ma position GPS actuelle</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Idéal si le coursier vous rejoint directement sur place</p>
                      </div>
                    </button>

                    {savedAddresses.length > 0 && (
                      <div className="space-y-1.5 pt-1">
                        <p className="text-[11px] font-medium text-slate-400">Carnet d'adresses configuré :</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {savedAddresses.map((addr) => {
                            const val = getAddressValue(addr);
                            const isPick = orderData.pickupAddress === val;
                            return (
                              <button
                                key={addr.id}
                                type="button"
                                onClick={() => setOrderData({ ...orderData, pickupAddress: val })}
                                className={`p-2.5 border rounded-xl text-left transition-all flex items-center space-x-2.5 truncate ${isPick ? 'border-slate-900 bg-slate-50' : 'border-slate-100 hover:border-slate-200'
                                  }`}
                              >
                                <i className={`${getAddressIcon(addr)} text-slate-400 text-sm`}></i>
                                <div className="truncate">
                                  <p className="text-xs font-bold text-slate-700 truncate">{getAddressName(addr)}</p>
                                  <p className="text-[10px] text-slate-400 truncate">{val}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <textarea
                      value={orderData.pickupAddress}
                      onChange={(e) => setOrderData({ ...orderData, pickupAddress: e.target.value })}
                      placeholder="Indiquez précisément le quartier, carrefour ou instructions de repère pour le retrait..."
                      rows={2}
                      className="w-full text-xs px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 transition-colors placeholder:text-slate-300 resize-none"
                    />
                  </div>

                  {/* Destination */}
                  <div className="space-y-2">
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Destination de livraison</label>
                    {savedAddresses.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {savedAddresses.map((addr) => {
                          const val = getAddressValue(addr);
                          const isDest = orderData.deliveryAddress === val;
                          return (
                            <button
                              key={addr.id}
                              type="button"
                              onClick={() => setOrderData({ ...orderData, deliveryAddress: val })}
                              className={`p-2.5 border rounded-xl text-left transition-all flex items-center space-x-2.5 truncate ${isDest ? 'border-slate-900 bg-slate-50' : 'border-slate-100 hover:border-slate-200'
                                }`}
                            >
                              <i className={`${getAddressIcon(addr)} text-slate-400 text-sm`}></i>
                              <div className="truncate">
                                <p className="text-xs font-bold text-slate-700 truncate">{getAddressName(addr)}</p>
                                <p className="text-[10px] text-slate-400 truncate">{val}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <textarea
                      value={orderData.deliveryAddress}
                      onChange={(e) => setOrderData({ ...orderData, deliveryAddress: e.target.value })}
                      placeholder="Quartier, porte, contact secondaire du destinataire..."
                      rows={2}
                      className="w-full text-xs px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 transition-colors placeholder:text-slate-300 resize-none"
                      required
                    />
                  </div>
                </div>
              )}

              {/* ÉTAPE 5 : DIMENSIONS & COLISAGE */}
              {currentStep === 5 && (
                <div className="space-y-5">
                  {/* Taille */}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Volume global</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {PACKAGE_SIZES.map((size) => {
                        const isSel = orderData.packageDetails.size === size.id;
                        return (
                          <button
                            key={size.id}
                            type="button"
                            onClick={() => setOrderData({ ...orderData, packageDetails: { ...orderData.packageDetails, size: size.id } })}
                            className={`p-3 border rounded-xl text-center transition-all flex flex-col items-center justify-center ${isSel ? 'border-slate-900 bg-slate-50' : 'border-slate-100 hover:border-slate-200'
                              }`}
                          >
                            <i className={`${size.icon} text-lg text-slate-500 mb-1`}></i>
                            <p className="text-xs font-bold text-slate-800">{size.name}</p>
                            <p className="text-[10px] text-slate-400">{size.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Poids */}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Poids estimé</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {PACKAGE_WEIGHTS.map((weight) => {
                        const isSel = orderData.packageDetails.weight === weight.id;
                        return (
                          <button
                            key={weight.id}
                            type="button"
                            onClick={() => setOrderData({ ...orderData, packageDetails: { ...orderData.packageDetails, weight: weight.id } })}
                            className={`p-3 border rounded-xl text-center transition-all ${isSel ? 'border-slate-900 bg-slate-50 font-semibold' : 'border-slate-100 hover:border-slate-200'
                              }`}
                          >
                            <p className="text-xs text-slate-800">{weight.name}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{weight.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Particularités du contenu</label>
                    <textarea
                      value={orderData.packageDetails.specialInstructions}
                      onChange={(e) => setOrderData({ ...orderData, packageDetails: { ...orderData.packageDetails, specialInstructions: e.target.value } })}
                      placeholder="Ex: Contient des liquides, denrées périssables, verres fragiles..."
                      rows={3}
                      maxLength={500}
                      className="w-full text-xs px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-slate-900 transition-colors placeholder:text-slate-300 resize-none"
                    />
                    <div className="text-[10px] text-slate-400 text-right mt-1">{orderData.packageDetails.specialInstructions.length}/500 car.</div>
                  </div>

                  {/* Assurance / Valeur déclarée */}
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 space-y-3">
                    <div className="flex items-start space-x-2">
                      <i className="ri-shield-check-line text-amber-600 mt-0.5"></i>
                      <div>
                        <h4 className="text-xs font-bold text-amber-900">Assurance & Valeur déclarée</h4>
                        <p className="text-[10px] text-amber-700 mt-0.5">Protégez vos colis de valeur en cas de perte ou d'endommagement</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="insurance"
                        checked={orderData.packageDetails.insuranceEnabled}
                        onChange={(e) => setOrderData({ ...orderData, packageDetails: { ...orderData.packageDetails, insuranceEnabled: e.target.checked } })}
                        className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                      />
                      <label htmlFor="insurance" className="text-xs font-semibold text-amber-800">Activer l'assurance (5% de la valeur déclarée)</label>
                    </div>
                    {orderData.packageDetails.insuranceEnabled && (
                      <div>
                        <label className="block text-[10px] font-bold text-amber-800 mb-1">Valeur du colis (GNF)</label>
                        <input
                          type="number"
                          value={orderData.packageDetails.declaredValue || ''}
                          onChange={(e) => setOrderData({ ...orderData, packageDetails: { ...orderData.packageDetails, declaredValue: e.target.value ? parseInt(e.target.value) : undefined } })}
                          placeholder="Ex: 500000"
                          className="w-full px-3 py-2 border border-amber-200 rounded-lg text-xs focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ÉTAPE 6 : FINANCEMENT & RECAP */}
              {currentStep === 6 && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">Passer au règlement</h3>
                  <div className="space-y-2">
                    {PAYMENT_METHODS.map((method) => {
                      const isSel = orderData.paymentMethod === method.id;
                      return (
                        <div
                          key={method.id}
                          onClick={() => setOrderData({ ...orderData, paymentMethod: method.id })}
                          className={`p-3 border rounded-xl cursor-pointer transition-all flex items-center justify-between ${isSel ? 'border-slate-900 bg-slate-50' : 'border-slate-100 hover:border-slate-200'
                            }`}
                        >
                          <div className="flex items-center space-x-3 min-w-0">
                            <div className={`w-9 h-9 ${method.color} rounded-xl flex items-center justify-center text-white text-base flex-shrink-0 shadow-sm`}>
                              <i className={method.icon}></i>
                            </div>
                            <div className="truncate">
                              <h4 className="text-xs font-bold text-slate-800">{method.name}</h4>
                              <p className="text-[11px] text-slate-400 truncate mt-0.5">{method.description}</p>
                            </div>
                          </div>
                          {isSel && <i className="ri-checkbox-circle-fill text-teal-600 text-lg ml-2"></i>}
                        </div>
                      );
                    })}
                  </div>

                  {/* Facture d'estimation visuelle */}
                  <div className="bg-slate-900 rounded-xl p-4 text-white space-y-2.5">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Bilan d'expédition</h4>
                    <div className="divide-y divide-slate-800 text-xs">
                      <div className="flex justify-between py-1.5">
                        <span className="text-slate-400">Portée :</span>
                        <span className="font-medium text-slate-200">
                          {GEOGRAPHIC_SCOPES.find(s => s.id === orderData.geographicScope)?.name || 'Non spécifié'}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5">
                        <span className="text-slate-400">Profil livreur :</span>
                        <span className="font-medium text-slate-200">
                          {DELIVERY_PROFILES.find(p => p.id === orderData.deliveryProfile)?.name || 'Non spécifié'}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5">
                        <span className="text-slate-400">Type :</span>
                        <span className="font-medium text-slate-200">
                          {DELIVERY_TYPES.find(t => t.id === orderData.deliveryType)?.name || 'Non spécifié'}
                        </span>
                      </div>
                      <div className="flex justify-between py-1.5">
                        <span className="text-slate-400">Gabarit :</span>
                        <span className="font-medium text-slate-200">
                          {PACKAGE_SIZES.find(s => s.id === orderData.packageDetails.size)?.name || 'Non spécifié'}
                        </span>
                      </div>
                      {orderData.packageDetails.insuranceEnabled && orderData.packageDetails.declaredValue && (
                        <div className="flex justify-between py-1.5">
                          <span className="text-slate-400">Assurance (5%) :</span>
                          <span className="font-medium text-amber-400">
                            {(orderData.packageDetails.declaredValue * 0.05).toLocaleString()} GNF
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 mt-1 border-t border-slate-800">
                        <span className="font-bold">Tarif estimé global :</span>
                        <span className="text-teal-400 font-mono font-bold">{calculatePrice().toLocaleString()} GNF</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer des Actions Clés */}
        {!successTrackingCode && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 1 || loading}
              className="px-4 py-2 border border-slate-200 text-slate-700 bg-white rounded-xl text-xs font-bold hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Retour
            </button>

            {currentStep < 6 ? (
              <button
                onClick={nextStep}
                className="px-5 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all shadow-sm"
              >
                Continuer
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!orderData.paymentMethod || loading}
                className="px-5 py-2 bg-teal-500 hover:bg-teal-400 text-slate-950 rounded-xl text-xs font-bold transition-all disabled:opacity-40 shadow-sm flex items-center space-x-1.5"
              >
                {loading && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                <span>{loading ? 'Création...' : 'Confirmer l\'envoi'}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}